import { useEffect, useLayoutEffect, useMemo, useState, useSyncExternalStore } from 'react'
import { api, ApiError, type LinkedSession, NICK_KEY, setSessionToken, storage } from '../api'
import { type MessageKey, t, useI18n } from '../i18n'
import { detectCaps, Player } from '../player/Player'
import { invite, onCompactLayout, setPresence, type Presence } from '../discord/sdk'
import { Avatar } from '../ui/Avatar'
import { promptDialog, showValueDialog } from '../ui/Dialog'
import { BrowserLink } from './BrowserLink'
import { Icon } from '../ui/Icon'
import { LangSwitch } from '../ui/LangSwitch'
import { Chat } from './Chat'
import { RoomClient, type RoomState, sourceTabKey } from './client'
import { Notice, Requests } from './Prompts'
import { Seats } from './Seats'
import { SourcePanel } from './SourcePanel'

export function RoomPage({ roomId }: { roomId: string }) {
  const { t } = useI18n()
  const [nick, setNick] = useState(() => storage.get(NICK_KEY) ?? '')
  const [joined, setJoined] = useState(() => !!storage.get(NICK_KEY))
  const [room, setRoom] = useState<{ name: string } | null>(null)
  const [error, setError] = useState<MessageKey | null>(null)
  // Someone who came from the Discord Activity is signed in as themselves.
  const [linked, setLinked] = useState(() => loadLinked(roomId))
  const [code] = useState(handoffCode)
  const [linkExpired, setLinkExpired] = useState(false)

  useEffect(() => {
    if (!code) return
    let alive = true
    redeem(code)
      .then((s) => {
        saveLinked(roomId, s)
        if (alive) setLinked(s)
      })
      .catch(() => alive && setLinkExpired(true))
    return () => {
      alive = false
    }
  }, [code, roomId])
  // Only this room's tab speaks as the linked user. A layout effect: it
  // must run before the room's own effects connect.
  useLayoutEffect(() => {
    if (!linked) return
    setSessionToken(linked.sessionToken)
    return () => setSessionToken(null)
  }, [linked])

  useEffect(() => {
    api
      .room(roomId)
      .then(setRoom)
      .catch((e) => setError(e instanceof ApiError && e.status === 404 ? 'room.notFound' : 'room.serverDown'))
  }, [roomId])

  if (error) return <Message title={t(error)} />
  if (!room || (code && !linked && !linkExpired)) return <Message title={t('room.opening')} />
  if (linked) return <Room roomId={roomId} nick={linked.nickname} linked />
  if (!joined) {
    return (
      <JoinForm
        roomName={room.name}
        nick={nick}
        notice={linkExpired ? t('join.linkExpired') : undefined}
        onJoin={(n) => {
          storage.set(NICK_KEY, n)
          setNick(n)
          setJoined(true)
        }}
      />
    )
  }
  return <Room roomId={roomId} nick={nick} />
}

function JoinForm({ roomName, nick, notice, onJoin }: { roomName: string; nick: string; notice?: string; onJoin: (nick: string) => void }) {
  const { t } = useI18n()
  const [value, setValue] = useState(nick)
  const [busy, setBusy] = useState(false)
  return (
    <main className="gate">
      <form
        className="gate__card"
        onSubmit={async (e) => {
          e.preventDefault()
          const n = value.trim()
          if (!n) return
          setBusy(true)
          try {
            await api.session()
            await api.setNickname(n)
            onJoin(n)
          } finally {
            setBusy(false)
          }
        }}
      >
        <div className="gate__top">
          <p className="gate__room">{roomName}</p>
          <LangSwitch />
        </div>
        <h1 className="gate__title">{t('join.title')}</h1>
        {notice && <p className="gate__notice">{notice}</p>}
        <label className="field">
          <span>{t('join.name')}</span>
          <input value={value} onChange={(e) => setValue(e.target.value)} maxLength={32} autoFocus required />
        </label>
        <button type="submit" className="btn btn--primary btn--wide" disabled={busy || !value.trim()}>
          {t('join.submit')}
        </button>
        <p className="muted small">{t('join.remember')}</p>
      </form>
    </main>
  )
}

// The handed-off session lives as long as the tab, like the link it came by.
const LINKED_KEY = 'chillio.linked.'

function loadLinked(roomId: string): LinkedSession | null {
  try {
    const v = sessionStorage.getItem(LINKED_KEY + roomId)
    return v ? (JSON.parse(v) as LinkedSession) : null
  } catch {
    return null
  }
}

function saveLinked(roomId: string, s: LinkedSession) {
  try {
    sessionStorage.setItem(LINKED_KEY + roomId, JSON.stringify(s))
  } catch {
    // the tab still works until it is reloaded
  }
}

let takenCode: string | null | undefined

/** Takes the one-time code of #login=… off the address, once. */
function handoffCode(): string | null {
  if (takenCode === undefined) {
    takenCode = new URLSearchParams(location.hash.slice(1)).get('login')
    if (takenCode) history.replaceState(history.state, '', location.pathname + location.search)
  }
  return takenCode
}

// A code works once; effects may run twice in development.
const redeeming = new Map<string, Promise<LinkedSession>>()

function redeem(code: string) {
  let p = redeeming.get(code)
  if (!p) {
    p = api.redeemHandoff(code)
    redeeming.set(code, p)
  }
  return p
}

const FULLSCREEN_TIP_KEY = 'chillio.discordFullscreenTip'

const closedText: Record<NonNullable<RoomState['closedReason']>, MessageKey> = {
  kicked: 'room.kicked',
  deleted: 'room.deleted',
  not_found: 'room.gone',
  banned: 'room.banned',
}

/**
 * The room itself. discord: inside the Activity; linked: on the website as
 * a Discord user who came from it — in both the name comes from Discord.
 */
export function Room({ roomId, nick, discord = false, linked = false }: { roomId: string; nick: string; discord?: boolean; linked?: boolean }) {
  const { t, lang } = useI18n()
  const [ready, setReady] = useState(false)
  const client = useMemo(() => new RoomClient(roomId, nick, detectCaps()), [roomId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false
    // The session cookie must exist before the WebSocket connects.
    api
      .session()
      .then(() => {
        if (cancelled) return
        setReady(true)
        client.connect()
      })
      .catch(() => setReady(true))
    return () => {
      cancelled = true
      client.close()
    }
  }, [client])

  const state = useSyncExternalStore(client.subscribe, client.getState)
  const me = state.members.find((m) => m.userId === state.you?.userId)

  // Discord shows the activity small in picture-in-picture or as a call tile.
  const [compact, setCompact] = useState(false)
  // Theater mode: only the picture, in place of full screen inside Discord.
  const [theater, setTheater] = useState(false)
  // Discord keeps its panels around the activity: the first time theater
  // mode is on, the website is suggested for full screen.
  useEffect(() => {
    if (!discord || !theater || storage.get(FULLSCREEN_TIP_KEY)) return
    storage.set(FULLSCREEN_TIP_KEY, '1')
    client.notify('discord.fullscreenTip')
  }, [discord, theater, client])
  useEffect(() => (discord ? onCompactLayout(setCompact) : undefined), [discord])

  // "Watching ..." in people's Discord profiles.
  const presence = discord ? presenceOf(state, lang) : null
  useEffect(() => {
    if (discord) setPresence(presence)
  }, [discord, presence?.title, presence?.state, presence?.start]) // eslint-disable-line react-hooks/exhaustive-deps
  const [tab, setTab] = useState<'chat' | 'source'>('chat')
  const [copied, setCopied] = useState(false)
  // An owner without a torrent needs the form on the source tab first.
  const ownerWithoutSource = state.you?.role === 'owner' && state.connection === 'open' && !state.source
  useEffect(() => {
    if (ownerWithoutSource) setTab('source')
  }, [ownerWithoutSource])

  if (!ready) return <Message title={t('room.connecting')} home={!discord} />
  if (state.closedReason) return <Message title={t(closedText[state.closedReason])} home={!discord} />

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(location.href)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      void showValueDialog({ title: t('room.copyLink'), value: location.href })
    }
  }

  const rename = async () => {
    const n = (await promptDialog({ title: t('room.renamePrompt'), value: storage.get(NICK_KEY) ?? '', maxLength: 32, confirmLabel: t('dialog.save') }))?.trim()
    if (n) {
      storage.set(NICK_KEY, n.slice(0, 32))
      client.setNick(n.slice(0, 32))
    }
  }

  return (
    <div className={`room${compact || theater ? ' room--compact' : ''}`}>
      <header className="room__header">
        {discord ? (
          <span className="logo">chillio</span>
        ) : (
          <a href="/" className="logo" aria-label={t('room.homeAria')}>
            chillio
          </a>
        )}
        <h1 className="room__name">{state.room?.name}</h1>
        {state.media?.info && <span className="room__title muted">{state.media.info.name}</span>}
        <span className="room__spacer" />
        {state.connection !== 'open' && <span className="room__conn">{t('room.reconnecting')}</span>}
        <span className="room__lang">
          <LangSwitch />
        </span>
        {discord || linked ? (
          <span className="room__me">
            <Avatar src={me?.avatar} name={client.nickOf(state.you?.userId ?? '')} className="room__me-avatar" />
            {client.nickOf(state.you?.userId ?? '')}
          </span>
        ) : (
          <button type="button" className="text-btn" onClick={rename} title={t('room.rename')}>
            {client.nickOf(state.you?.userId ?? '')}
          </button>
        )}
        {discord ? (
          <>
            <BrowserLink roomId={roomId} name={client.nickOf(state.you?.userId ?? '')} />
            <button type="button" className="btn btn--small" onClick={invite}>
              <Icon name="voice" />
              <span className="btn__label">{t('room.invite')}</span>
            </button>
          </>
        ) : (
          <>
            <button type="button" className="btn btn--small" onClick={copyLink} aria-label={t('room.copyLinkAria')}>
              <Icon name={copied ? 'check' : 'link'} />
              <span className="btn__label">{copied ? t('room.copied') : t('room.copyLink')}</span>
            </button>
          </>
        )}
      </header>
      <div className="room__main">
        <div className="room__stage">
          <Player client={client} state={state} theater={theater} onTheater={setTheater} />
          <Seats client={client} state={state} />
        </div>
        <aside className="room__side">
          <div className="tabs" role="tablist">
            <button type="button" role="tab" aria-selected={tab === 'chat'} onClick={() => setTab('chat')}>
              {t('room.tabChat')}
            </button>
            <button type="button" role="tab" aria-selected={tab === 'source'} onClick={() => setTab('source')}>
              {t(sourceTabKey(state))}
            </button>
          </div>
          <div className="room__panel">{tab === 'chat' ? <Chat client={client} state={state} /> : <SourcePanel client={client} state={state} />}</div>
        </aside>
      </div>
      <Requests client={client} state={state} />
      <Notice state={state} />
    </div>
  )
}

function Message({ title, home = true }: { title: string; home?: boolean }) {
  const { t } = useI18n()
  return (
    <main className="gate">
      <div className="gate__card">
        <p className="gate__title">{title}</p>
        {home && (
          <a className="text-btn" href="/">
            {t('common.home')}
          </a>
        )}
      </div>
    </main>
  )
}

/**
 * What the room shows in Discord profiles: the film, and while it plays,
 * the time left. Start is rounded so that the presence is not resent on
 * every tick.
 */
function presenceOf(state: RoomState, lang: string): Presence | null {
  const info = state.media?.info
  if (!info) return null
  const catalog = state.title
  const title = (catalog && (catalog.titles[lang] || catalog.titles.original)) || info.name
  const pb = state.playback
  if (pb.status !== 'playing') return { title, state: t('presence.paused') }
  const start = Math.round((pb.anchor - pb.pos * 1000) / 10_000) * 10_000
  return { title, state: t('presence.watching'), start, end: start + Math.round(info.duration * 1000) }
}
